import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { errorMsg } from '../../../../../Apis/functions';
import { STATUS_MODEL } from '../../../../../enums/statusEnum';
import HandleChange from '../../../../../Helpers/HandleChange';
import { permissionObj } from '../../../../../Helpers/permission';
import { ApplyDecisionRequest, AssignEmployeeRequest } from '../../../../../reduxToolkit/RequestAccess/RequestAccessApi';
import routes from '../../../../../Routes/routes';
import { GUARD_REQUEST_MODEL, MODAL_OPTIONS } from '../Model/Model';

/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * Hook-like component to manage request actions for assigning hosts or rejecting requests.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.setDetailsModal - Function to toggle the details modal visibility.
 * @param {Object} props.itemSelected - The currently selected request item.
 * @param {Function} props.setModified -Callback to notify the parent component that data has changed.
 * @returns {Object} State variables and handlers for managing modals, actions, and form data.
 * @returns {boolean} return.isHostModal - Whether the assign host modal is open.
 * @returns {boolean} return.isReject - Whether the reject request modal is open.
 * @returns {Array} return.selectedHost - Array with the selected host.
 * @returns {Function} return.setSelectedHost - Function to update selectedHost.
 * @returns {boolean} return.cleanChipSearchHost - Flag to reset host search input.
 * @returns {Function} return.setCleanChipSearchHost - Function to update cleanChipSearchHost.
 * @returns {Object} return.data - Form data model for rejection.
 * @returns {Function} return.handleChange - Handler to update form data fields.
 * @returns {Function} return.handleOpenModalHost - Open assign host modal.
 * @returns {Function} return.handleCloseModalHost - Close assign host modal.
 * @returns {Function} return.openRejectModal - Open reject request modal.
 * @returns {Function} return.closeRejectModal - Close reject request modal.
 * @returns {Function} return.handleAction - Handle assigning or rejecting a request.
 * @returns {boolean} return.emptyFields - Indicates if required fields are empty.
 * @returns {boolean} return.hasPermissions - Flag for user permissions on request actions.
 */

export const RequestTabApp = ({ setDetailsModal, itemSelected, setModified }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    const [isHostModal, setIsHostModal] = useState(false);
    const [isReject, setIsReject] = useState(false);
    const [selectedHost, setSelectedHost] = useState([]);
    const [cleanChipSearchHost, setCleanChipSearchHost] = useState(false);
    const [data, setData] = useState(GUARD_REQUEST_MODEL);
    const { handleChange } = HandleChange({ model: data, setModel: setData });
    const [emptyFields, setEmptyFields] = useState(false)
    const hasPermissions = (DEV || permission?.includes(permissionObj?.WEB_REQUEST_ACCESS_MANAGE))

    // --- Helpers ---
    const toggleModal = (modalSetter, state) => {
        setDetailsModal(!state);
        modalSetter(state);
    };

    const handleResponse = ({ success, code }, successMsg) => {
        if (success) {
            setModified(true)
            setDetailsModal(false);
            setIsHostModal(false);
            setIsReject(false);
            toast.success(successMsg);
        } else {
            errorMsg(code);
        }
    };

    //handle action type: "assign" | "reject"
    const handleAction = (type) => {
        let body;
        let action;
        let successMessage;

        if (type === MODAL_OPTIONS.ASSIGN) {
            if (selectedHost.length === 0) {
                setEmptyFields(true)
                return;
            }
            body = { id: itemSelected?.id, assignedToId: selectedHost[0]?.id };
            action = AssignEmployeeRequest;
            successMessage = t("has_been_assigned_successfully");
        } else {
            if (!data.isValidReject()) {
                setEmptyFields(true)
                return;
            }
            body = {
                id: itemSelected?.id,
                reason: data?.comment,
                statusId: STATUS_MODEL.DENIED,
            };
            action = ApplyDecisionRequest;
            successMessage = t("has_been_denied_successfully");
        }

        dispatch(action(body)).then(({ payload: { data } }) =>
            handleResponse(data, successMessage)
        );
    };

    useEffect(() => {
        if (selectedHost.length) {
            setEmptyFields(false)
        }

    }, [selectedHost])


    useEffect(() => {
        if (!isHostModal) setSelectedHost([]);
    }, [isHostModal]);

    //If don't have permissions redirec to to Forbidden page
    if (!DEV &&
        !permission?.includes(permissionObj?.WEB_REQUEST_ACCESS_READ)
    ) navigate(routes.forbidden);

    useEffect(() => {
        if (isReject || isHostModal) setEmptyFields(false);
    }, [isReject, isHostModal]);


    return {

        isHostModal,
        isReject,
        selectedHost,
        setSelectedHost,
        cleanChipSearchHost,
        setCleanChipSearchHost,
        data,
        handleChange,
        // modals
        handleOpenModalHost: () => toggleModal(setIsHostModal, true),
        handleCloseModalHost: () => toggleModal(setIsHostModal, false),
        openRejectModal: () => toggleModal(setIsReject, true),
        closeRejectModal: () => toggleModal(setIsReject, false),
        // actions
        handleAction,
        emptyFields,
        hasPermissions
    };
};
